import React, { useState, useEffect, useRef } from 'react';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Check, MagnifyingGlass, X } from '@phosphor-icons/react';
import { searchNaf } from '../services/api';

/**
 * Autocomplete NAF/APE : recherche dynamique par code ou libellé.
 * Props: value = {code, label} | null, onChange({code, label} | null), testId
 */
export function NafAutocomplete({ value, onChange, testId = 'naf', disabled = false }) {
  const [query, setQuery] = useState('');
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debRef = useRef(null);
  useEffect(() => {
    if (disabled) { setOpen(false); setItems([]); setLoading(false); }
  }, [disabled]);

  useEffect(() => {
    let active = true;
    if (debRef.current) clearTimeout(debRef.current);
    if (disabled || !open) return;
    debRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await searchNaf(query, 15);
        if (active) setItems(res.data.items || []);
      } catch {
        if (active) setItems([]);
      } finally {
        if (active) setLoading(false);
      }
    }, 250);
    return () => { active = false; if (debRef.current) clearTimeout(debRef.current); };
  }, [query, open, disabled]);

  if (value?.code) {
    return (
      <div className="flex min-w-0 items-center gap-2" data-testid={testId} aria-disabled={disabled}>
        <Badge variant="outline" className="min-w-0 max-w-full border-[#2D5A4C]/40 text-[#2D5A4C] gap-1.5" data-testid={`${testId}-selected`}>
          <span className="font-mono font-semibold shrink-0">{value.code}</span>
          <span className="text-[#5E6D66]">·</span>
          <span className="min-w-0 truncate max-w-[280px]" title={value.label}>{value.label}</span>
          <button
            type="button"
            disabled={disabled}
            className="ml-1 shrink-0 hover:bg-[#2D5A4C]/10 rounded disabled:cursor-not-allowed"
            onClick={() => { if (!disabled) onChange(null); }}
            aria-label="Retirer"
            data-testid={`${testId}-clear`}
          >
            <X size={12} />
          </button>
        </Badge>
      </div>
    );
  }

  return (
    <div className="space-y-1" data-testid={testId}>
      <div className="relative">
        <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#5E6D66]" />
        <Input
          disabled={disabled}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => { if (!disabled) setOpen(true); }}
          placeholder="Code (ex : 10.71C) ou activité (ex : boulanger)"
          className="pl-8"
          data-testid={`${testId}-input`}
        />
      </div>
      {open && !disabled && (query || items.length > 0) && (
        <div data-testid={`${testId}-options`} className="border border-[#E8E6E1] rounded-md bg-white shadow-sm max-h-[240px] overflow-y-auto">
          {loading && <div data-testid={`${testId}-loading`} className="px-3 py-2 text-sm text-[#5E6D66]">Recherche…</div>}
          {!loading && items.length === 0 && query && (
            <div data-testid={`${testId}-empty`} className="px-3 py-2 text-sm text-[#5E6D66]">Aucun code NAF trouvé</div>
          )}
          {items.map((it) => (
            <button
              key={it.code}
              type="button"
              onClick={() => {
                if (disabled) return;
                onChange({ code: it.code, label: it.label });
                setOpen(false); setQuery('');
              }}
              className="w-full text-left px-3 py-2 hover:bg-[#F9F8F5] flex items-start gap-2 text-sm"
              data-testid={`${testId}-opt-${it.code}`}
            >
              <span className="font-mono font-semibold text-[#2D5A4C] w-16 flex-shrink-0">{it.code}</span>
              <span className="text-[#1A2421]">{it.label}</span>
              <Check size={0} className="hidden" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default NafAutocomplete;
